const PLAID_SCRIPT = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
export const PLAID_OAUTH_TOKEN_KEY = "moneymap.plaid.oauth.link-token";
export const PLAID_OAUTH_RETURN_PATH_KEY = "moneymap.plaid.oauth.return-path";

interface PlaidLinkError {
  readonly error_code?: string;
  readonly error_message?: string;
}

interface PlaidHandler {
  open(): void;
  destroy(): void;
}

interface PlaidFactory {
  create(configuration: {
    readonly token: string;
    readonly receivedRedirectUri?: string;
    readonly onSuccess: (publicToken: string) => void;
    readonly onExit: (error: PlaidLinkError | null) => void;
  }): PlaidHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidFactory;
  }
}

let loadPromise: Promise<PlaidFactory> | undefined;

export function loadPlaidLink(): Promise<PlaidFactory> {
  if (window.Plaid) return Promise.resolve(window.Plaid);
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<PlaidFactory>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PLAID_SCRIPT}"]`);
    const script = existing ?? document.createElement("script");
    const done = () =>
      window.Plaid ? resolve(window.Plaid) : reject(new Error("Plaid Link did not initialize."));
    script.addEventListener("load", done, { once: true });
    script.addEventListener("error", () => reject(new Error("Plaid Link could not load.")), {
      once: true,
    });
    if (!existing) {
      script.src = PLAID_SCRIPT;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.append(script);
    }
  });
  return loadPromise;
}

export async function openPlaidLink(
  linkToken: string,
  receivedRedirectUri?: string,
): Promise<string | null> {
  const plaid = await loadPlaidLink();
  return new Promise<string | null>((resolve, reject) => {
    const handler = plaid.create({
      token: linkToken,
      ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
      onSuccess(publicToken) {
        handler.destroy();
        resolve(publicToken);
      },
      onExit(error) {
        handler.destroy();
        if (error) reject(new Error(error.error_message ?? error.error_code ?? "Plaid closed."));
        else resolve(null);
      },
    });
    handler.open();
  });
}
